import {
  ID,
  LinkedItemSection,
  newTaskSuffix,
  noGroupingId,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import { filterEmptyChildrenCascading } from './filter-empty-children-cascading';

export type BuildSectionTreeOptions = {
  tasks: Task[];
  sections?: LinkedItemSection[] | null;
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  noSectionLabel: string;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildSectionTree = ({
  tasks,
  sections,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  noSectionLabel,
  rootLabel,
  newTaskLabel,
}: BuildSectionTreeOptions): Record<ID, TaskListTreeItem> => {
  const _sections = sections ?? [];

  const sectionTreeItems = _sections.reduce<Record<ID, TaskListTreeItem>>(
    (acc, section) => {
      acc[section.id] = {
        id: section.id,
        type: TaskListItemTypes.Section,
        name: section.name,
        children: [],
        isFolder: true,
      };
      return acc;
    },
    {},
  );

  if (_sections.length) {
    sectionTreeItems[noGroupingId] = {
      id: noGroupingId,
      type: TaskListItemTypes.Section,
      name: noSectionLabel,
      children: [],
      isFolder: true,
    };
  }

  const sectionIds = Object.keys(sectionTreeItems);

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? _sections.length
        ? sectionIds.reduce<ID[]>((acc, sectionId) => {
            const order = customOrder?.[sectionId] ?? [];
            acc.push(...order);
            return acc;
          }, [])
        : customOrder?.[noGroupingId] ?? []
      : [];

  const sortedTasks = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  });

  if (!_sections.length) {
    const taskItems: Record<ID, TaskListTreeItem> = {};
    sortedTasks.forEach((task) => {
      taskItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };
    });

    const children = [...sortedTasks.map((t) => t.id)];

    if (!hideNewTaskRow) {
      const newTaskItemId = `${noGroupingId}${newTaskSuffix}`;
      taskItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
      children.push(newTaskItemId);
    }

    return {
      [rootId]: {
        id: rootId,
        name: rootLabel,
        type: TaskListItemTypes.Root,
        children,
      },
      ...taskItems,
    };
  }

  const tasksInSections = new Set<ID>();

  _sections.forEach((section) => {
    const sectionTaskIds = new Set(section.tasks);
    const tasksForThisSection = sortedTasks.filter((t) =>
      sectionTaskIds.has(t.id),
    );
    tasksForThisSection.forEach((task) => {
      sectionTreeItems[section.id].children?.push(task.id);
      tasksInSections.add(task.id);
    });
  });

  const noSectionTasks = sortedTasks.filter((t) => !tasksInSections.has(t.id));
  noSectionTasks.forEach((task) => {
    sectionTreeItems[noGroupingId].children?.push(task.id);
  });

  const allTreeItems: Record<ID, TaskListTreeItem> = { ...sectionTreeItems };
  sortedTasks.forEach((task) => {
    allTreeItems[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
  });

  if (!hideNewTaskRow) {
    Object.keys(sectionTreeItems).forEach((sectionId) => {
      const sectionItem = allTreeItems[sectionId];
      if (!sectionItem) {
        return;
      }
      if (!sectionItem.children?.length && !showEmptyGroups) {
        return;
      }

      const newTaskItemId = `${sectionId}${newTaskSuffix}`;
      sectionItem.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
  const topLevelIds = Object.keys(filteredTreeItems).filter(
    (id) => filteredTreeItems[id].type === TaskListItemTypes.Section,
  );

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: topLevelIds,
    },
    ...filteredTreeItems,
  };
};
